// A FEJLESZTÉS-BOARD — a folyamat oszlopai: Ötlet · Tervezett · Fejlesztés alatt · Kiadva.
//
// MIÉRT BOARD ÉS NEM TÁBLA: a lap eddig két füles táblát mutatott (útiterv / változásnapló), ami két
// ADATHALMAZNAK látszott, pedig egy folyamat. (Élő adaton mérve a változásnapló-fül ÜRES volt: 47
// tételből nulla kiadott.) A board azt mondja meg egy pillantásra, ami a kérdés: hol tart a munka.
//
// A „KIADVA" OSZLOP a FRISS véget mutatja (a BFF `board` nézete vágja) — a teljes kiadás-történet az
// idővonalon él. Enélkül a jobb szélen évekig nőne egy lista, amit soha senki nem görget.
//
// A FÜGGŐ ÉS AZ ELVETETT tétel NEM kap ÁLLANDÓ oszlopot, hanem lábléc-jelzést a szűrőre: egy elvetett ötlet
// nem az, amin dolgozunk — de elérhetőnek kell maradnia. Két holt oszlop a képernyő szélén ugyanilyen rossz
// válasz. HA VISZONT a felhasználó kifejezetten rájuk szűr, a board ÁTVÁLT az adott fázis egyetlen
// oszlopára (`boardColumnsFor`) — különben a szűrés négy ÜRES oszlopot adna, és a sorok némán eltűnnének
// (mért hiba: a lábléc saját linkje vitt pont ebbe az állapotba).
//
// A DRAG & DROP SZÁNDÉKOSAN NINCS: az írás-művelet (fázis-váltás + sorrend) optimistic UI-t és
// ütközés-kezelést kíván — külön kör. A board enélkül is megválaszolja a fő kérdést.
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProductUpdateCard,
  type ProductUpdateCardItem,
} from '@/components/product-updates/product-update-card';
import { badgeVisual } from '@/components/shared/domain-badge';
import { Badge } from '@/components/ui/badge';
import { Text } from '@/components/ui/text';
import {
  boardColumnsFor,
  isOffBoardStage,
  offBoardItems,
  toBoardColumns,
} from '@/features/product-updates/views';

export interface BoardRow extends ProductUpdateCardItem {
  horizon?: string | null;
  targetPeriod?: string | null;
  shippedAt?: string | null;
}

export interface ProductUpdateBoardProps<T extends BoardRow> {
  rows: readonly T[];
  title: (row: T) => string;
  meta: (row: T) => ReactNode;
  onOpen?: ((row: T) => void) | undefined;
  actions?: ((row: T) => ReactNode) | undefined;
  /** A lábléc-jelzés kattintása — a hívó a fázis-szűrőt állítja rá. */
  onShowOffBoard?: ((stage: string) => void) | undefined;
  /** Az ÉPPEN aktív fázis-szűrő (`null` = nincs). Ez dönti el, kap-e oszlopot egy boardon-kívüli fázis. */
  stageFilter?: string | null | undefined;
  /** A fázis-szűrő törlése — a boardon-kívüli nézetből visszavezető út. */
  onClearStageFilter?: (() => void) | undefined;
}

/** Az oszlop-fejléc fázis-ikonja. Ismeretlen fázisnál nem renderel (a felirat egyedül is megáll). */
function StageIcon({ stage }: { stage: string }): JSX.Element | null {
  const Icon = badgeVisual('productUpdateStage', stage)?.icon;
  return Icon === undefined ? null : <Icon className="text-subtle size-4 shrink-0" aria-hidden />;
}

export function ProductUpdateBoard<T extends BoardRow>({
  rows,
  title,
  meta,
  onOpen,
  actions,
  onShowOffBoard,
  stageFilter,
  onClearStageFilter,
}: ProductUpdateBoardProps<T>): JSX.Element {
  const { t } = useTranslation();
  // BOARDON-KÍVÜLI NÉZET: a szűrő kifejezetten a szünetelő/elvetett tételekre mutat, tehát azok kapnak
  // oszlopot. A lábléc-jelzés ilyenkor önmagára mutatna, ezért helyette a VISSZAVEZETŐ út áll ott.
  const offBoardView = isOffBoardStage(stageFilter);
  const columns = toBoardColumns(rows, boardColumnsFor(stageFilter), (r) => r.stage);
  const offBoard = offBoardView ? [] : offBoardItems(rows);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* AZ OSZLOPOK EGYMÁS MELLETT állnak, mindig — ez a kanban bevett alakja, és a folyamat IRÁNYA
          maga az információ. Ha nem férnek ki, a board VÍZSZINTESEN GÖRGET (fix oszlop-szélesség +
          `shrink-0`), nem tördel új sorba: a tördelt board megtöri az olvasás-irányt, és a „hol tart a
          munka" kérdésre nem egy pillantással válaszol. A `grow` miatt széles képernyőn KITÖLTIK a helyet,
          a `shrink-0` miatt viszont soha nem mennek a `w-60` alá — onnantól görgetés. A 15rem-es alsó határ mérve
          van: a négy oszlop ÉPP kifér az 1280-as kötelező töréspontnál, alatta (1024 / 768) görget.

          A MOBIL nem ezt kapja: ott a hívó az idővonalra vált (a csonkolt, oldalra húzható board telefonon
          használhatatlan). */}
      <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto">
        {columns.map((col) => (
          <section
            key={col.key}
            aria-label={t(`productUpdates.stage.${col.key}`)}
            className="bg-sunken/40 flex min-h-0 w-60 shrink-0 grow flex-col rounded-lg"
          >
            <header className="flex items-center gap-2 px-3 pb-1.5 pt-2.5">
              {/* A fázis IKONJA a közös badge-katalógusból jön (`domain-badge`), nem egy helyi térképből:
                  a fogalom jele a rendszerben EGY helyen dől el. A fejléc maga nem badge — az oszlop-cím
                  overline-tipográfia —, ezért csak a jelet kérjük el, a tónust nem. */}
              <StageIcon stage={col.key} />
              <Text variant="overline" as="h3">
                {t(`productUpdates.stage.${col.key}`)}
              </Text>
              <Badge variant="secondary">{col.items.length}</Badge>
            </header>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2 pt-0.5">
              {col.items.length === 0 ? (
                <p className="text-subtle px-1 py-3 text-center text-xs">
                  {t('productUpdates.board.emptyColumn')}
                </p>
              ) : (
                col.items.map((row) => (
                  <ProductUpdateCard
                    key={row.id}
                    item={row}
                    title={title(row)}
                    meta={meta(row)}
                    onOpen={onOpen === undefined ? undefined : () => onOpen(row)}
                    actions={actions?.(row)}
                  />
                ))
              )}
            </div>
          </section>
        ))}
      </div>

      {offBoardView && (
        <p className="text-subtle px-1 pb-1 text-xs">
          {t('productUpdates.board.offBoardView')}{' '}
          {onClearStageFilter !== undefined && (
            <button
              type="button"
              className="hover:text-foreground underline underline-offset-2"
              onClick={onClearStageFilter}
            >
              {t('productUpdates.board.backToBoard')}
            </button>
          )}
        </p>
      )}

      {offBoard.length > 0 && (
        <p className="text-subtle px-1 pb-1 text-xs">
          {t('productUpdates.board.offBoard', { count: offBoard.length })}{' '}
          {onShowOffBoard !== undefined && (
            <>
              <button
                type="button"
                className="hover:text-foreground underline underline-offset-2"
                onClick={() => onShowOffBoard('on_hold')}
              >
                {t('productUpdates.stage.on_hold')}
              </button>
              {' · '}
              <button
                type="button"
                className="hover:text-foreground underline underline-offset-2"
                onClick={() => onShowOffBoard('cancelled')}
              >
                {t('productUpdates.stage.cancelled')}
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}
